import { useEffect, useState } from 'preact/hooks';
import { api, Child } from '../services/api';
import { LoadingCard } from '../components/LoadingSpinner';

interface ChildWithFamily extends Child {
  family: {
    id: string;
    parentName: string;
    email: string;
    phone: string;
    emergencyContactName?: string;
    emergencyContactPhone?: string;
  };
}

interface NewChild {
  first_name: string;
  last_name: string;
  birthdate: string;
  residence: string;
  allergies: string;
  medical_notes: string;
  special_instructions: string;
}

interface NewFamily {
  parent_name: string;
  email: string;
  phone: string;
  residence: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  notes: string;
  children: NewChild[];
}

export default function FamiliesPage() {
  const [children, setChildren] = useState<ChildWithFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'age' | 'residence'>('name');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('active');
  const [minAge, setMinAge] = useState<string>('');
  const [maxAge, setMaxAge] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Add family modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingFamily, setAddingFamily] = useState(false);
  const [addError, setAddError] = useState('');

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'child' | 'family'; id: string; name: string } | null>(null);

  const [newFamily, setNewFamily] = useState<NewFamily>({
    parent_name: '',
    email: '',
    phone: '',
    residence: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    notes: '',
    children: [
      {
        first_name: '',
        last_name: '',
        birthdate: '',
        residence: '',
        allergies: '',
        medical_notes: '',
        special_instructions: '',
      },
    ],
  });

  useEffect(() => {
    fetchFamilies();
  }, []);

  async function fetchFamilies() {
    setLoading(true);
    setError('');
    try {
      const data = await api.getFamilies();
      if (data.success) {
        const allChildren: ChildWithFamily[] = [];
        data.families.forEach((family) => {
          family.children.forEach((child) => {
            allChildren.push({
              ...child,
              family: {
                id: family.id,
                parentName: family.parentName,
                email: family.email,
                phone: family.phone,
                emergencyContactName: family.emergencyContactName,
                emergencyContactPhone: family.emergencyContactPhone,
              },
            });
          });
        });
        setChildren(allChildren);
      } else {
        setError('Failed to fetch children');
      }
    } catch {
      setError('Failed to fetch children');
    } finally {
      setLoading(false);
    }
  }

  const handleAddFamily = async (e: Event) => {
    e.preventDefault();
    setAddingFamily(true);
    setAddError('');

    try {
      if (!newFamily.parent_name.trim() || !newFamily.email.trim()) {
        setAddError('Parent name and email are required');
        return;
      }

      if (newFamily.children.some((child) => !child.first_name.trim() || !child.last_name.trim() || !child.birthdate)) {
        setAddError('All children must have first name, last name, and birthdate');
        return;
      }

      const result = await api.addFamily({
        parent_name: newFamily.parent_name.trim(),
        email: newFamily.email.trim(),
        phone: newFamily.phone.trim() || undefined,
        emergency_contact_name: newFamily.emergency_contact_name.trim() || undefined,
        emergency_contact_phone: newFamily.emergency_contact_phone.trim() || undefined,
        notes: newFamily.notes.trim() || undefined,
        children: newFamily.children.map((child) => ({
          first_name: child.first_name.trim(),
          last_name: child.last_name.trim(),
          birthdate: child.birthdate,
          residence: child.residence.trim() || undefined,
          allergies: child.allergies.trim() || undefined,
          medical_notes: child.medical_notes.trim() || undefined,
          special_instructions: child.special_instructions.trim() || undefined,
        })),
      });

      if (result.success && result.family) {
        await fetchFamilies();
        setShowAddModal(false);
        setNewFamily({
          parent_name: '',
          email: '',
          phone: '',
          residence: '',
          emergency_contact_name: '',
          emergency_contact_phone: '',
          notes: '',
          children: [
            {
              first_name: '',
              last_name: '',
              birthdate: '',
              residence: '',
              allergies: '',
              medical_notes: '',
              special_instructions: '',
            },
          ],
        });
      } else {
        setAddError(result.message || 'Failed to add family');
      }
    } catch (err: any) {
      setAddError(err.message || 'Failed to add family');
    } finally {
      setAddingFamily(false);
    }
  };

  const addChild = () => {
    setNewFamily((prev) => ({
      ...prev,
      children: [
        ...prev.children,
        {
          first_name: '',
          last_name: '',
          birthdate: '',
          residence: '',
          allergies: '',
          medical_notes: '',
          special_instructions: '',
        },
      ],
    }));
  };

  const removeChild = (index: number) => {
    setNewFamily((prev) => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index),
    }));
  };

  const updateChild = (index: number, field: keyof NewChild, value: string) => {
    setNewFamily((prev) => ({
      ...prev,
      children: prev.children.map((child, i) => (i === index ? { ...child, [field]: value } : child)),
    }));
  };

  const openDeleteModal = (type: 'child' | 'family', id: string, name: string) => {
    setDeleteTarget({ type, id, name });
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    setDeleteError('');

    try {
      const result = await api.deleteFamily(deleteTarget.id);

      if (result.success) {
        setShowDeleteModal(false);
        setDeleteTarget(null);
        await fetchFamilies();
      } else {
        setDeleteError(result.message || 'Failed to delete');
      }
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const toggleRowExpansion = (childId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(childId)) {
        next.delete(childId);
      } else {
        next.add(childId);
      }
      return next;
    });
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSortBy('name');
    setFilterStatus('active');
    setMinAge('');
    setMaxAge('');
  };

  const getChildAge = (birthdate: string) => {
    const today = new Date();
    const birthDate = new Date(birthdate);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const filteredAndSortedChildren = children
    .filter((child) => {
      if (searchTerm) {
        const query = searchTerm.toLowerCase();
        const matchesName = `${child.firstName} ${child.lastName}`.toLowerCase().includes(query);
        const matchesParent = child.family.parentName.toLowerCase().includes(query);
        const matchesResidence = (child.residence || '').toLowerCase().includes(query);
        if (!matchesName && !matchesParent && !matchesResidence) return false;
      }

      if (filterStatus !== 'all') {
        if (child.status !== filterStatus) return false;
      }

      if (minAge || maxAge) {
        const age = getChildAge(child.birthdate);
        if (minAge && age < parseInt(minAge)) return false;
        if (maxAge && age > parseInt(maxAge)) return false;
      }

      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        case 'age':
          return new Date(a.birthdate).getTime() - new Date(b.birthdate).getTime();
        case 'residence':
          return (a.residence || '').localeCompare(b.residence || '');
        default:
          return 0;
      }
    });

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', padding: '36px 24px 60px', display: 'flex', flexDirection: 'column', gap: 24 }}>
      
      {/* ── Page Header ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <p className="mini-label" style={{ margin: '0 0 4px' }}>Care Directory</p>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 30,
              fontWeight: 600,
              letterSpacing: '-0.04em',
              color: 'var(--ink)',
              margin: 0,
            }}
          >
            Registered Children
          </h1>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--ink-soft)', margin: '4px 0 0' }}>
            Directory of all children and family contacts registered in your church.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 999,
              background: 'var(--leaf-soft)',
              color: '#2F7A4E',
            }}
          >
            {filteredAndSortedChildren.length} {filteredAndSortedChildren.length === 1 ? 'Child' : 'Children'}
          </span>

          <button onClick={() => setShowAddModal(true)} className="btn-primary" style={{ padding: '8px 18px', fontSize: 13 }}>
            + Add Family
          </button>
        </div>
      </div>

      {/* ── Search & Filter Toolbar (Soft Minimalism) ── */}
      <div
        style={{
          background: 'var(--paper)',
          border: '1px solid rgba(25, 38, 49, 0.08)',
          borderRadius: 20,
          padding: '20px 24px',
          boxShadow: '0 2px 8px rgba(25, 38, 49, 0.02)',
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, alignItems: 'flex-end' }}>
          {/* Search input */}
          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-faint)', marginBottom: 6 }}>
              Search Directory
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by child, parent, or residence..."
                value={searchTerm}
                onChange={(e) => setSearchTerm((e.target as HTMLInputElement).value)}
                style={{
                  width: '100%',
                  background: 'rgba(25, 38, 49, 0.03)',
                  border: '1px solid rgba(25, 38, 49, 0.1)',
                  borderRadius: 12,
                  padding: '8px 14px',
                  fontSize: 13,
                  color: 'var(--ink)',
                  outline: 'none',
                }}
              />
            </div>
          </div>

          {/* Sort selector */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-faint)', marginBottom: 6 }}>
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy((e.target as HTMLSelectElement).value as any)}
              style={{
                width: '100%',
                background: 'rgba(25, 38, 49, 0.03)',
                border: '1px solid rgba(25, 38, 49, 0.1)',
                borderRadius: 12,
                padding: '8px 14px',
                fontSize: 13,
                color: 'var(--ink)',
                outline: 'none',
              }}
            >
              <option value="name">Child Name</option>
              <option value="age">Age (Youngest First)</option>
              <option value="residence">Residence</option>
            </select>
          </div>

          {/* Status selector */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-faint)', marginBottom: 6 }}>
              Status
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus((e.target as HTMLSelectElement).value as any)}
                style={{
                  flex: 1,
                  background: 'rgba(25, 38, 49, 0.03)',
                  border: '1px solid rgba(25, 38, 49, 0.1)',
                  borderRadius: 12,
                  padding: '8px 14px',
                  fontSize: 13,
                  color: 'var(--ink)',
                  outline: 'none',
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <button
                onClick={resetFilters}
                className="btn-secondary"
                style={{ padding: '8px 12px', fontSize: 12 }}
                title="Reset filters"
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="alert-error" style={{ borderRadius: 14 }}>
          <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20" style={{ flexShrink: 0 }}>
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <div>
            <p style={{ fontWeight: 600, margin: 0 }}>Error loading children</p>
            <p style={{ fontSize: 13, opacity: 0.8, margin: '2px 0 0' }}>{error}</p>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <LoadingCard />
          <LoadingCard />
          <LoadingCard />
        </div>
      )}

      {/* ── Full-Length Accordion List (NO CARDS) ── */}
      {!loading && (
        <div
          style={{
            background: 'var(--paper)',
            border: '1px solid rgba(25, 38, 49, 0.08)',
            borderRadius: 20,
            boxShadow: '0 2px 8px rgba(25, 38, 49, 0.02)',
            overflow: 'hidden',
          }}
        >
          {filteredAndSortedChildren.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '56px 24px', color: 'var(--ink-soft)' }}>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, margin: 0, color: 'var(--ink)' }}>
                No children found
              </p>
              <p style={{ fontSize: 13, color: 'var(--ink-faint)', margin: '6px 0 0' }}>
                {searchTerm ? 'Try adjusting your search terms.' : 'No children have been registered yet.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {filteredAndSortedChildren.map((child, index) => {
                const isExpanded = expandedRows.has(child.id);
                const age = getChildAge(child.birthdate);

                return (
                  <div
                    key={child.id}
                    style={{
                      borderBottom: index < filteredAndSortedChildren.length - 1 ? '1px solid rgba(25, 38, 49, 0.06)' : 'none',
                    }}
                  >
                    {/* ── Accordion Row Header (Full Length Bar) ── */}
                    <button
                      onClick={() => toggleRowExpansion(child.id)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '18px 26px',
                        background: isExpanded ? 'rgba(25, 38, 49, 0.015)' : 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'background 120ms ease',
                      }}
                      onMouseEnter={(e) => {
                        if (!isExpanded) (e.currentTarget as HTMLElement).style.background = 'rgba(25, 38, 49, 0.01)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isExpanded) (e.currentTarget as HTMLElement).style.background = 'transparent';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <h3
                            style={{
                              fontFamily: 'var(--font-display)',
                              fontSize: 16,
                              fontWeight: 600,
                              letterSpacing: '-0.02em',
                              color: 'var(--ink)',
                              margin: 0,
                            }}
                          >
                            {child.firstName} {child.lastName}
                          </h3>

                          {/* Age pill */}
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: 999,
                              background: 'rgba(25, 38, 49, 0.05)',
                              color: 'var(--ink-soft)',
                            }}
                          >
                            Age {age}
                          </span>

                          {/* Status pill */}
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: 999,
                              background: child.status === 'active' ? 'var(--leaf-soft)' : 'rgba(25, 38, 49, 0.06)',
                              color: child.status === 'active' ? '#2F7A4E' : 'var(--ink-soft)',
                            }}
                          >
                            {child.status}
                          </span>
                        </div>

                        {/* Parent & Residence summary */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', marginRight: 16 }}>
                          <span style={{ fontSize: 13, color: 'var(--ink-soft)', fontWeight: 500 }}>
                            {child.family.parentName}
                          </span>
                          {child.residence && (
                            <>
                              <span style={{ color: 'var(--ink-faint)' }}>•</span>
                              <span style={{ fontSize: 12, color: 'var(--ink-faint)' }}>
                                {child.residence}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Expand / Collapse Chevron */}
                      <div
                        style={{
                          color: 'var(--ink-faint)',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 200ms cubic-bezier(0.23, 1, 0.32, 1)',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>

                    {/* ── Accordion Expanded Body (Full Length) ── */}
                    {isExpanded && (
                      <div
                        style={{
                          padding: '18px 28px 24px',
                          background: 'rgba(25, 38, 49, 0.015)',
                          borderTop: '1px solid rgba(25, 38, 49, 0.04)',
                        }}
                      >
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>
                          
                          {/* Section 1: Special Information */}
                          <div>
                            <p className="mini-label" style={{ margin: '0 0 8px' }}>Special Information</p>
                            {(child.allergies || child.medicalNotes || child.specialInstructions) ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {child.allergies && (
                                  <div
                                    style={{
                                      padding: '8px 12px',
                                      borderRadius: 10,
                                      background: 'var(--coral-soft)',
                                      color: 'var(--coral)',
                                      fontSize: 12,
                                    }}
                                  >
                                    <strong style={{ fontWeight: 600 }}>Allergies:</strong> {child.allergies}
                                  </div>
                                )}
                                {child.medicalNotes && (
                                  <div
                                    style={{
                                      padding: '8px 12px',
                                      borderRadius: 10,
                                      background: 'var(--sky-soft)',
                                      color: '#3B7A92',
                                      fontSize: 12,
                                    }}
                                  >
                                    <strong style={{ fontWeight: 600 }}>Medical:</strong> {child.medicalNotes}
                                  </div>
                                )}
                                {child.specialInstructions && (
                                  <div
                                    style={{
                                      padding: '8px 12px',
                                      borderRadius: 10,
                                      background: 'var(--card-tint-cream)',
                                      color: '#8C6207',
                                      fontSize: 12,
                                    }}
                                  >
                                    <strong style={{ fontWeight: 600 }}>Instructions:</strong> {child.specialInstructions}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p style={{ fontSize: 13, color: 'var(--ink-faint)', margin: 0 }}>
                                None recorded
                              </p>
                            )}
                          </div>

                          {/* Section 2: Parent & Family Contact */}
                          <div>
                            <p className="mini-label" style={{ margin: '0 0 8px' }}>Parent & Family Contact</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                              <p style={{ margin: 0, fontWeight: 600, color: 'var(--ink)' }}>
                                {child.family.parentName}
                              </p>
                              <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
                                {child.family.email}
                              </p>
                              {child.family.phone && (
                                <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
                                  {child.family.phone}
                                </p>
                              )}
                              {child.residence && (
                                <p style={{ margin: 0, color: 'var(--ink-faint)', fontSize: 12 }}>
                                  Residence: {child.residence}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Section 3: Emergency & Actions */}
                          <div>
                            <p className="mini-label" style={{ margin: '0 0 8px' }}>Emergency & Record</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                              {child.family.emergencyContactName ? (
                                <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
                                  <strong style={{ fontWeight: 600, color: 'var(--ink)' }}>Contact:</strong> {child.family.emergencyContactName} {child.family.emergencyContactPhone && `(${child.family.emergencyContactPhone})`}
                                </p>
                              ) : (
                                <p style={{ margin: 0, color: 'var(--ink-faint)', fontSize: 12 }}>No emergency contact listed</p>
                              )}
                              <p style={{ margin: '4px 0 10px', color: 'var(--ink-faint)', fontSize: 12 }}>
                                Born: {new Date(child.birthdate).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>

                              <button
                                onClick={() => openDeleteModal('family', child.family.id, `${child.firstName} ${child.lastName}'s family`)}
                                className="btn-danger"
                                style={{ alignSelf: 'flex-start', padding: '5px 12px', fontSize: 12 }}
                              >
                                Delete Family
                              </button>
                            </div>
                          </div>

                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Add Family Modal ── */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowAddModal(false);
          }}
        >
          <div
            style={{
              background: 'var(--paper)',
              borderRadius: 24,
              border: '1px solid rgba(25, 38, 49, 0.1)',
              boxShadow: '0 24px 60px rgba(25, 38, 49, 0.15)',
              width: '100%',
              maxWidth: 720,
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '28px 32px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600, color: 'var(--ink)', margin: 0 }}>
                Add New Family
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-faint)' }}
              >
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleAddFamily} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {addError && (
                <div className="alert-error" style={{ borderRadius: 12 }}>
                  <p style={{ margin: 0, fontSize: 13 }}>{addError}</p>
                </div>
              )}

              {/* Parent Info */}
              <div style={{ background: 'rgba(25, 38, 49, 0.02)', border: '1px solid rgba(25, 38, 49, 0.06)', borderRadius: 16, padding: '18px 20px' }}>
                <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--ink)', margin: '0 0 14px' }}>
                  Parent Information
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink-soft)', marginBottom: 4 }}>
                      Parent Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="input text-sm"
                      value={newFamily.parent_name}
                      onChange={(e) => setNewFamily((prev) => ({ ...prev, parent_name: (e.target as HTMLInputElement).value }))}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink-soft)', marginBottom: 4 }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      className="input text-sm"
                      value={newFamily.email}
                      onChange={(e) => setNewFamily((prev) => ({ ...prev, email: (e.target as HTMLInputElement).value }))}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink-soft)', marginBottom: 4 }}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      className="input text-sm"
                      value={newFamily.phone}
                      onChange={(e) => setNewFamily((prev) => ({ ...prev, phone: (e.target as HTMLInputElement).value }))}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink-soft)', marginBottom: 4 }}>
                      Residence
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Karen, Nairobi"
                      className="input text-sm"
                      value={newFamily.residence}
                      onChange={(e) => setNewFamily((prev) => ({ ...prev, residence: (e.target as HTMLInputElement).value }))}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink-soft)', marginBottom: 4 }}>
                      Emergency Contact Name
                    </label>
                    <input
                      type="text"
                      className="input text-sm"
                      value={newFamily.emergency_contact_name}
                      onChange={(e) => setNewFamily((prev) => ({ ...prev, emergency_contact_name: (e.target as HTMLInputElement).value }))}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink-soft)', marginBottom: 4 }}>
                      Emergency Contact Phone
                    </label>
                    <input
                      type="tel"
                      className="input text-sm"
                      value={newFamily.emergency_contact_phone}
                      onChange={(e) => setNewFamily((prev) => ({ ...prev, emergency_contact_phone: (e.target as HTMLInputElement).value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Children info */}
              <div style={{ background: 'rgba(25, 38, 49, 0.02)', border: '1px solid rgba(25, 38, 49, 0.06)', borderRadius: 16, padding: '18px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--ink)', margin: 0 }}>
                    Children
                  </h4>
                  <button type="button" onClick={addChild} className="btn-secondary" style={{ padding: '4px 12px', fontSize: 12 }}>
                    + Add Another Child
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {newFamily.children.map((child, index) => (
                    <div
                      key={index}
                      style={{
                        background: 'var(--paper)',
                        border: '1px solid rgba(25, 38, 49, 0.08)',
                        borderRadius: 14,
                        padding: '16px 18px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-faint)', textTransform: 'uppercase' }}>
                          Child {index + 1}
                        </span>
                        {newFamily.children.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeChild(index)}
                            style={{ background: 'transparent', border: 'none', color: 'var(--coral)', cursor: 'pointer', fontSize: 12 }}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                        <div>
                          <label style={{ display: 'block', fontSize: 11, color: 'var(--ink-soft)', marginBottom: 2 }}>First Name *</label>
                          <input
                            type="text"
                            required
                            className="input text-sm"
                            value={child.first_name}
                            onChange={(e) => updateChild(index, 'first_name', (e.target as HTMLInputElement).value)}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: 11, color: 'var(--ink-soft)', marginBottom: 2 }}>Last Name *</label>
                          <input
                            type="text"
                            required
                            className="input text-sm"
                            value={child.last_name}
                            onChange={(e) => updateChild(index, 'last_name', (e.target as HTMLInputElement).value)}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: 11, color: 'var(--ink-soft)', marginBottom: 2 }}>Birthdate *</label>
                          <input
                            type="date"
                            required
                            className="input text-sm"
                            value={child.birthdate}
                            onChange={(e) => updateChild(index, 'birthdate', (e.target as HTMLInputElement).value)}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: 11, color: 'var(--ink-soft)', marginBottom: 2 }}>Allergies</label>
                          <input
                            type="text"
                            placeholder="None or list items"
                            className="input text-sm"
                            value={child.allergies}
                            onChange={(e) => updateChild(index, 'allergies', (e.target as HTMLInputElement).value)}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: 11, color: 'var(--ink-soft)', marginBottom: 2 }}>Medical Notes</label>
                          <input
                            type="text"
                            className="input text-sm"
                            value={child.medical_notes}
                            onChange={(e) => updateChild(index, 'medical_notes', (e.target as HTMLInputElement).value)}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: 11, color: 'var(--ink-soft)', marginBottom: 2 }}>Special Instructions</label>
                          <input
                            type="text"
                            className="input text-sm"
                            value={child.special_instructions}
                            onChange={(e) => updateChild(index, 'special_instructions', (e.target as HTMLInputElement).value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, paddingTop: 10 }}>
                <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary" disabled={addingFamily}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={addingFamily}>
                  {addingFamily ? 'Saving...' : 'Save Family'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {showDeleteModal && deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowDeleteModal(false);
          }}
        >
          <div
            style={{
              background: 'var(--paper)',
              borderRadius: 24,
              border: '1px solid rgba(25, 38, 49, 0.1)',
              boxShadow: '0 24px 60px rgba(25, 38, 49, 0.15)',
              width: '100%',
              maxWidth: 420,
              padding: '28px 28px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'var(--coral-soft)',
                color: 'var(--coral)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>

            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--ink)', margin: '0 0 8px' }}>
              Confirm Delete
            </h3>
            <p style={{ fontSize: 14, color: 'var(--ink-soft)', margin: '0 0 4px' }}>
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
            </p>
            <p style={{ fontSize: 12, color: 'var(--coral)', margin: '0 0 20px' }}>
              This will permanently delete the family and all associated children.
            </p>

            {deleteError && (
              <div className="alert-error" style={{ borderRadius: 10, marginBottom: 16 }}>
                <p style={{ margin: 0, fontSize: 12 }}>{deleteError}</p>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <button onClick={() => setShowDeleteModal(false)} className="btn-secondary" disabled={deleting}>
                Cancel
              </button>
              <button onClick={handleDelete} className="btn-danger" disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
